import { LucideFileText, PinIcon } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  pinDocument,
  selectIsAtSoftLimit,
  selectPinnedDocs,
} from '@/services/slices/pinnedDocsSlice';
import { Blob } from '@/services/types/blob-types';

type EmptyStateVariant = 'no-selection' | 'not-found' | 'multiple';

interface EmptyStateProps {
  variant: EmptyStateVariant;
  count?: number;
  /** Selected blobs for "Pin All" functionality (only for 'multiple' variant) */
  selectedBlobs?: Blob[];
  /** Matter ID for pinning */
  matterId?: string;
  /** Matter name for pinning */
  matterName?: string;
}

export const EmptyState = ({
  variant,
  count = 0,
  selectedBlobs,
  matterId,
  matterName,
}: EmptyStateProps) => {
  const dispatch = useDispatch();
  const pinnedDocs = useSelector(selectPinnedDocs);
  const isAtSoftLimit = useSelector(selectIsAtSoftLimit);

  const content = {
    'no-selection': {
      title: 'No item selected',
      description: 'Select a file or folder to view its details',
    },
    'not-found': {
      title: 'Item not found',
      description: 'The selected item could not be found',
    },
    multiple: {
      title: 'Multiple files selected',
      description: `${count} documents selected`,
    },
  };

  const { title, description } = content[variant];

  // Filter selected blobs to only PDFs that aren't already pinned
  const pinnablePdfs =
    selectedBlobs?.filter(
      (blob) =>
        blob.file_mime_type === 'application/pdf' &&
        !pinnedDocs.some((doc) => doc.id === blob.id),
    ) || [];

  const handlePinAllPdfs = () => {
    if (!matterId || !matterName) {
      toast.error('Unable to pin: Matter context not available');
      return;
    }

    if (pinnablePdfs.length === 0) {
      toast.info('No new PDFs to pin', {
        description: 'Selected PDFs are already pinned or no PDFs selected',
      });
      return;
    }

    pinnablePdfs.forEach((blob) => {
      dispatch(
        pinDocument({
          id: blob.id,
          matterId,
          matterName,
          fileName: blob.name,
          fileType: blob.file_mime_type || 'application/pdf',
          downloadUrl: blob.download_url,
          pinnedAt: new Date().toISOString(),
        }),
      );
    });

    toast.success(
      `Pinned ${pinnablePdfs.length} PDF${pinnablePdfs.length > 1 ? 's' : ''} to dock`,
    );

    if (isAtSoftLimit) {
      toast.info('Consider unpinning some documents', {
        description: 'You have many documents pinned',
      });
    }
  };

  // Count PDFs in selection (including already pinned)
  const totalPdfsInSelection =
    selectedBlobs?.filter((blob) => blob.file_mime_type === 'application/pdf')
      .length || 0;

  const canPinPdfs =
    variant === 'multiple' && matterId && matterName && pinnablePdfs.length > 0;

  return (
    <div className="h-full flex flex-col justify-center items-center p-6">
      <div className="text-center flex flex-col items-center gap-4">
        {variant === 'multiple' ? (
          <div className="relative">
            <LucideFileText className="h-12 w-12 text-muted-foreground" />
            <div className="absolute -right-2 -top-1 px-2 py-1 rounded-full bg-primary text-xs text-primary-foreground font-medium">
              {count}
            </div>
          </div>
        ) : (
          <LucideFileText className="h-12 w-12 text-muted-foreground" />
        )}
        <h2 className="text-lg font-medium">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>

        {/* Pin All PDFs button for multiple selection */}
        {variant === 'multiple' && totalPdfsInSelection > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handlePinAllPdfs}
            disabled={!canPinPdfs}
            className="mt-2 text-violet-600 hover:text-violet-700 border-violet-200 hover:border-violet-300 hover:bg-violet-50"
          >
            <PinIcon className="h-3.5 w-3.5 mr-1.5" />
            Pin{' '}
            {pinnablePdfs.length > 0
              ? pinnablePdfs.length
              : totalPdfsInSelection}{' '}
            PDF
            {(pinnablePdfs.length > 0
              ? pinnablePdfs.length
              : totalPdfsInSelection) !== 1
              ? 's'
              : ''}
            {pinnablePdfs.length === 0 && totalPdfsInSelection > 0
              ? ' (already pinned)'
              : ''}
          </Button>
        )}
      </div>
    </div>
  );
};

export default EmptyState;
